import { INPUT_FIELD_TYPE, INPUT_TYPE } from '@components/DynamicFormField/constant';
import {
  CURRENCY_OPTIONS,
  PARTNER_OPTIONS,
  ACCOUNT_TYPE_OPTIONS,
  TRANSFER_TYPE_OPTIONS,
  TRANSFER_REFERENCE_ID_MAX_LENGTH
} from './constants';

// Account Form Fields
export const getCreateAccountFields = (accounts, corporateBeneficiaries, libraConfigs) => {
  // Use libraConfigs if provided, otherwise fallback to paginated data
  const beneficiariesList = libraConfigs?.corporateBeneficiaries || corporateBeneficiaries?.data?.items || [];

  const beneficiaryOptions = beneficiariesList.map((beneficiary) => ({
    label: `${beneficiary.accountName || beneficiary.label} (${beneficiary.corporateBeneficiaryId})`,
    value: beneficiary.corporateBeneficiaryId
  }));

  return [
  {
    name: 'uniqueAccountReference',
    label: 'Account Reference',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: true,
    isEditable: true,
    placeholder: 'e.g., GB-safeguarding'
  },
  {
    name: 'name',
    label: 'Account Name',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: true,
    isEditable: true,
    placeholder: 'e.g., GB Safeguarding Account'
  },
  {
    name: 'description',
    label: 'Description',
    fieldType: INPUT_FIELD_TYPE.TEXTAREA,
    isRequired: false,
    isEditable: true,
    placeholder: 'Enter account description'
  },
  {
    name: 'currency',
    label: 'Currency',
    fieldType: INPUT_FIELD_TYPE.SELECT,
    options: CURRENCY_OPTIONS,
    isRequired: true,
    isEditable: true
  },
  {
    name: 'partner',
    label: 'Partner',
    fieldType: INPUT_FIELD_TYPE.SELECT,
    options: PARTNER_OPTIONS,
    isRequired: true,
    isEditable: true
  },
  {
    name: 'accountType',
    label: 'Account Type',
    fieldType: INPUT_FIELD_TYPE.SELECT,
    options: ACCOUNT_TYPE_OPTIONS,
    isRequired: true,
    isEditable: true,
    dependsOn: { field: 'partner', value: 'CLEARBANK' }
  },
  {
    name: 'token',
    label: 'API Token',
    fieldType: INPUT_FIELD_TYPE.TEXTAREA,
    isRequired: false,
    isEditable: true,
    placeholder: 'Enter API token for Clearbank',
    dependsOn: { field: 'partner', value: 'CLEARBANK' }
  },
  {
    name: 'baseUrl',
    label: 'Base URL',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: false,
    isEditable: true,
    placeholder: 'e.g., https://institution-api.clearbank.co.uk',
    dependsOn: { field: 'partner', value: 'CLEARBANK' }
  },
  {
    name: 'privateKey',
    label: 'Private Key',
    fieldType: INPUT_FIELD_TYPE.TEXTAREA,
    isRequired: false,
    isEditable: true,
    placeholder: 'Enter private key',
    dependsOn: { field: 'partner', value: 'CLEARBANK' }
  },
  {
    name: 'legalAddress',
    label: 'Legal Address',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: false,
    isEditable: true,
    placeholder: 'e.g., London',
    dependsOn: { field: 'partner', value: 'CLEARBANK' }
  },
  {
    name: 'sourceBankAccountId',
    label: 'Source Bank Account ID',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: false,
    isEditable: true,
    placeholder: 'e.g., gb_safeguarding_account',
    dependsOn: { field: 'partner', value: 'CLEARBANK' }
  },
  {
    name: 'sweepoutEnabled',
    label: 'Enable Sweepout',
    fieldType: INPUT_FIELD_TYPE.SWITCH,
    isRequired: false,
    isEditable: true
  },
  {
    name: 'fundTransferStrategy',
    label: 'Fund Transfer Strategy',
    fieldType: INPUT_FIELD_TYPE.SELECT,
    options: [
      { value: 'PERCENTAGE_SPLIT', label: 'Percentage Split' },
      { value: 'SKIPPABLE_RATIO', label: 'Skippable Ratio' }
    ],
    isRequired: false,
    isEditable: true,
    placeholder: 'Select fund transfer strategy',
    dependsOn: { field: 'sweepoutEnabled', value: true }
  },
  {
    name: 'targetBankAccountIds',
    label: 'Target Bank Accounts (Beneficiaries)',
    fieldType: INPUT_FIELD_TYPE.DRAG_DROP,
    options: beneficiaryOptions,
    isRequired: false,
    isEditable: true,
    placeholder: 'Select target beneficiary accounts for sweepout',
    dependsOn: { field: 'sweepoutEnabled', value: true }
  },
  {
    name: 'percentages',
    label: 'Percentages/Ratios (comma-separated)',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: false,
    isEditable: true,
    placeholder: 'e.g., 50, 30, 20 (must match number of selected accounts)',
    dependsOn: { field: 'sweepoutEnabled', value: true }
  },
];
};

// Corporate Beneficiary Form Fields
export const createCorporateBeneficiaryFields = [
  {
    name: 'corporateBeneficiaryId',
    label: 'Beneficiary ID',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: true,
    isEditable: true,
    placeholder: 'e.g., red_envelope'
  },
  {
    name: 'accountName',
    label: 'Account Name',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: true,
    isEditable: true,
    placeholder: 'e.g., Infinity Alpha International Ltd'
  },
  {
    name: 'description',
    label: 'Description',
    fieldType: INPUT_FIELD_TYPE.TEXTAREA,
    isRequired: true,
    isEditable: true,
    placeholder: 'Enter beneficiary description'
  },
  {
    name: 'label',
    label: 'Label',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: true,
    isEditable: true,
    placeholder: 'e.g., red_envelope'
  },
  {
    name: 'currency',
    label: 'Currency',
    fieldType: INPUT_FIELD_TYPE.SELECT,
    options: CURRENCY_OPTIONS,
    isRequired: true,
    isEditable: true
  },
  {
    name: 'iban',
    label: 'IBAN',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: true,
    isEditable: true,
    placeholder: 'e.g., GB14BCPY04054100014848'
  },
  {
    name: 'accountHolderName',
    label: 'Account Holder Name',
    fieldType: INPUT_FIELD_TYPE.INPUT,
    inputType: INPUT_TYPE.STRING,
    isRequired: true,
    isEditable: true,
    placeholder: 'Enter account holder name'
  },
  {
    name: 'isActive',
    label: 'Is Active',
    fieldType: INPUT_FIELD_TYPE.SWITCH,
    isRequired: false,
    isEditable: true
  }
];

// Dynamic form field generators

// Corporate Beneficiary Form Fields
export const getCorporateBeneficiaryFields = (selectedTransferType) => {
  // Base fields (always shown)
  const baseFields = [
    {
      name: 'corporateBeneficiaryId',
      label: 'Beneficiary ID',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'e.g., red_envelope'
    },
    {
      name: 'accountName',
      label: 'Account Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'e.g., Infinity Alpha International Ltd'
    },
    {
      name: 'description',
      label: 'Description',
      fieldType: INPUT_FIELD_TYPE.TEXTAREA,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter beneficiary description'
    },
    {
      name: 'label',
      label: 'Label',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'e.g., red_envelope'
    },
    {
      name: 'currency',
      label: 'Currency',
      fieldType: INPUT_FIELD_TYPE.SELECT,
      options: CURRENCY_OPTIONS,
      isRequired: true,
      isEditable: true
    },
    {
      name: 'transferType',
      label: 'Transfer Type',
      fieldType: INPUT_FIELD_TYPE.SELECT,
      options: TRANSFER_TYPE_OPTIONS,
      isRequired: true,
      isEditable: true
    }
  ];

  // Fields specific to SEPA (IbanBankAccountDetails)
  const sepaFields = [
    {
      name: 'iban',
      label: 'IBAN',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'e.g., GB14BCPY04054100014848'
    },
    {
      name: 'bic',
      label: 'BIC (Bank Identifier Code)',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter BIC'
    },
    {
      name: 'bankName',
      label: 'Bank Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter bank name'
    },
    {
      name: 'accountHolderName',
      label: 'Account Holder Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account holder name'
    },
    {
      name: 'accountHolderAddress',
      label: 'Account Holder Address',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter account holder address'
    },
    {
      name: 'countryCode',
      label: 'Country Code',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'e.g., GB'
    }
  ];

  // Fields specific to FPS/CHAPS (FpsBankAccountDetails)
  const fpsFields = [
    {
      name: 'sortCode',
      label: 'Sort Code',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'e.g., 12-34-56'
    },
    {
      name: 'accountNumber',
      label: 'Account Number',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account number'
    },
    {
      name: 'iban',
      label: 'IBAN',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter IBAN (optional)'
    },
    {
      name: 'accountHolderName',
      label: 'Account Holder Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account holder name'
    },
    {
      name: 'bankName',
      label: 'Bank Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter bank name'
    },
    {
      name: 'reference',
      label: 'Payment Reference',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter payment reference'
    }
  ];

  // Fields specific to SWIFT (SwiftBankAccountDetails)
  const swiftFields = [
    {
      name: 'swiftCode',
      label: 'SWIFT Code',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter SWIFT/BIC code'
    },
    {
      name: 'bankName',
      label: 'Bank Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter bank name'
    },
    {
      name: 'bankAddress',
      label: 'Bank Address',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter bank address'
    },
    {
      name: 'accountNumber',
      label: 'Account Number',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account number'
    },
    {
      name: 'accountHolderName',
      label: 'Account Holder Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account holder name'
    },
    {
      name: 'accountHolderAddress',
      label: 'Account Holder Address',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter account holder address'
    },
    {
      name: 'correspondentBankSwift',
      label: 'Correspondent Bank SWIFT',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter correspondent bank SWIFT code'
    },
    {
      name: 'correspondentBankName',
      label: 'Correspondent Bank Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter correspondent bank name'
    },
    {
      name: 'lei',
      label: 'LEI (Legal Entity Identifier)',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter LEI'
    },
    {
      name: 'countryCode',
      label: 'Country Code',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'e.g., US'
    }
  ];

  // Fields specific to ACH (AchBankAccountDetails)
  const achFields = [
    {
      name: 'routingNumber',
      label: 'Routing Number (ABA)',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter ABA routing number'
    },
    {
      name: 'accountNumber',
      label: 'Account Number',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account number'
    },
    {
      name: 'accountHolderName',
      label: 'Account Holder Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account holder name'
    },
    {
      name: 'bankName',
      label: 'Bank Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter bank name'
    },
    {
      name: 'accountType',
      label: 'Account Type',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'e.g., CHECKING, SAVINGS'
    },
    {
      name: 'reference',
      label: 'Payment Reference',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter payment reference'
    }
  ];

  // Fields specific to WIRE (WireBankAccountDetails)
  const wireFields = [
    {
      name: 'swiftCode',
      label: 'SWIFT Code',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter SWIFT/BIC code'
    },
    {
      name: 'bankName',
      label: 'Bank Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter bank name'
    },
    {
      name: 'bankAddress',
      label: 'Bank Address',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter bank address'
    },
    {
      name: 'accountNumber',
      label: 'Account Number',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account number'
    },
    {
      name: 'accountHolderName',
      label: 'Account Holder Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter account holder name'
    },
    {
      name: 'accountHolderAddress',
      label: 'Account Holder Address',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter account holder address'
    },
    {
      name: 'correspondentBankSwift',
      label: 'Correspondent Bank SWIFT',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter correspondent bank SWIFT code'
    },
    {
      name: 'correspondentBankName',
      label: 'Correspondent Bank Name',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter correspondent bank name'
    },
    {
      name: 'lei',
      label: 'LEI (Legal Entity Identifier)',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter LEI'
    },
    {
      name: 'countryCode',
      label: 'Country Code',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: 'e.g., US'
    },
    {
      name: 'wireInstructions',
      label: 'Wire Instructions',
      fieldType: INPUT_FIELD_TYPE.TEXTAREA,
      isRequired: false,
      isEditable: true,
      placeholder: 'Enter additional wire transfer instructions'
    }
  ];

  // Fields specific to COLUMN_COUNTERPARTY (ColumnCounterpartyDetails)
  const columnCounterpartyFields = [
    {
      name: 'counterPartyId',
      label: 'Counterparty ID',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter counterparty ID'
    }
  ];

  // Combine fields based on selected transfer type
  let typeSpecificFields = [];

  switch (selectedTransferType) {
    case 'SEPA':
      typeSpecificFields = sepaFields;
      break;
    case 'FPS':
    case 'CHAPS':
      typeSpecificFields = fpsFields;
      break;
    case 'SWIFT':
      typeSpecificFields = swiftFields;
      break;
    case 'ACH':
      typeSpecificFields = achFields;
      break;
    case 'WIRE':
      typeSpecificFields = wireFields;
      break;
    case 'COLUMN_COUNTERPARTY':
      typeSpecificFields = columnCounterpartyFields;
      break;
    default:
      typeSpecificFields = [];
  }

  return [...baseFields, ...typeSpecificFields, {
    name: 'isActive',
    label: 'Is Active',
    fieldType: INPUT_FIELD_TYPE.SWITCH,
    isRequired: false,
    isEditable: true
  }];
};

// Scheduled Transfer Form Fields
export const getScheduledTransferFields = (accounts, corporateBeneficiaries, libraConfigs) => {
  // Use libraConfigs if provided, otherwise fallback to paginated data
  const accountsList = libraConfigs?.accounts || accounts?.data?.items || [];
  const beneficiariesList = libraConfigs?.corporateBeneficiaries || corporateBeneficiaries?.data?.items || [];

  const accountOptions = accountsList.map((account) => ({
    label: `${account.name} (${account.accountId}) - ${account.currency}`,
    value: account.accountId
  }));

  const beneficiaryOptions = beneficiariesList.map((beneficiary) => ({
    label: `${beneficiary.accountName || beneficiary.label} (${beneficiary.corporateBeneficiaryId})`,
    value: beneficiary.corporateBeneficiaryId
  }));

  return [
    {
      name: 'scheduledTransferType',
      label: 'Transfer Type',
      fieldType: INPUT_FIELD_TYPE.SELECT,
      options: [
        { value: 'ONE_TIME', label: 'One Time' },
        { value: 'RECURRENT', label: 'Recurrent' }
      ],
      isRequired: true,
      isEditable: true,
      placeholder: 'Select transfer type'
    },
    {
      name: 'accountId',
      label: 'Sender Account',
      fieldType: INPUT_FIELD_TYPE.SELECT,
      options: accountOptions,
      isRequired: true,
      isEditable: true,
      placeholder: 'Select an account'
    },
    {
      name: 'strategyType',
      label: 'Fund Transfer Strategy',
      fieldType: INPUT_FIELD_TYPE.SELECT,
      options: [
        { value: 'PERCENTAGE_SPLIT', label: 'Percentage Split' },
        { value: 'SKIPPABLE_RATIO', label: 'Skippable Ratio' }
      ],
      isRequired: true,
      isEditable: true,
      placeholder: 'Select strategy type'
    },
    {
      name: 'amountType',
      label: 'Amount Type',
      fieldType: INPUT_FIELD_TYPE.SELECT,
      options: [
        { value: 'FIXED', label: 'Fixed Amount' },
        { value: 'AVAILABLE_BALANCE', label: 'Available Balance' }
      ],
      isRequired: true,
      isEditable: true,
      placeholder: 'Select amount type'
    },
    {
      name: 'amount',
      label: 'Amount',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.NUMBER,
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter amount',
      dependsOn: { field: 'amountType', value: 'FIXED' }
    },
    {
      name: 'beneficiaryIds',
      label: 'Beneficiaries',
      fieldType: INPUT_FIELD_TYPE.DRAG_DROP,
      options: beneficiaryOptions,
      isRequired: true,
      isEditable: true,
      placeholder: 'Select beneficiaries for split'
    },
    {
      name: 'executionTime',
      label: 'Execution Time',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: 'datetime-local',
      isRequired: true,
      isEditable: true,
      placeholder: 'Select execution time',
      dependsOn: { field: 'scheduledTransferType', value: 'ONE_TIME' }
    },
    {
      name: 'cronExpression',
      label: 'Cron Expression',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: true,
      placeholder: 'e.g., 0 0 * * * (every day at midnight)',
      tooltipLabel: 'Cron format: "minute hour day month weekday"\nExamples:\n• "0 0 * * *" = Every day at midnight\n• "0 9 * * 1-5" = 9 AM on weekdays\n• "*/15 * * * *" = Every 15 minutes\n• "0 0 1 * *" = 1st of each month',
      dependsOn: { field: 'scheduledTransferType', value: 'RECURRENT' }
    }
  ];
};

// Create Transfer Form Fields
//
// Builds the dynamic field config for the Create Transfer modal. The
// receiver list is filtered to the sender's currency (we don't allow
// cross-currency transfers from this UI), and the sender account ID +
// currency are pre-filled from the chosen sender account so the user can't
// drift them.
export const getCreateTransferFields = ({
  libraConfigs,
  selectedAccountId
}) => {
  const accountOptions =
    libraConfigs?.accounts?.map((account) => ({
      label: account.name,
      description: [account.accountId, account.currency]
        .filter(Boolean)
        .join(' · '),
      value: account.accountId
    })) || [];

  const selectedAccount = libraConfigs?.accounts?.find(
    (account) => account.accountId === selectedAccountId
  );
  const selectedCurrency = selectedAccount?.currency || '';
  const senderAccountIdValue = selectedAccount?.accountId || '';
  const senderAccountReference = selectedAccount?.uniqueAccountReference || '';

  const filteredBeneficiaries = selectedAccountId
    ? libraConfigs?.corporateBeneficiaries?.filter(
        (beneficiary) => beneficiary.currency === selectedCurrency
      ) || []
    : libraConfigs?.corporateBeneficiaries || [];

  const bankAccountOptions = filteredBeneficiaries.map((beneficiary) => ({
    label: beneficiary.accountName,
    description: [beneficiary.corporateBeneficiaryId, beneficiary.currency]
      .filter(Boolean)
      .join(' · '),
    value: beneficiary.corporateBeneficiaryId
  }));

  return [
    {
      name: 'accountId',
      label: 'Sender Bank Account',
      fieldType: INPUT_FIELD_TYPE.SEARCHABLE_SELECT,
      options: accountOptions,
      isRequired: true,
      isEditable: true,
      placeholder: 'Select a sender account',
      emptyMessage: 'No accounts available'
    },
    {
      name: 'senderAccountIdDisplay',
      label: 'Sender Account ID',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: false,
      value: senderAccountIdValue,
      placeholder: senderAccountIdValue || 'Auto-filled from selected sender',
      tooltipLabel:
        'Read-only. Mirrors the account ID of the sender selected above.'
    },
    {
      name: 'bankAccountId',
      label: 'Receiver Bank Account',
      fieldType: INPUT_FIELD_TYPE.SEARCHABLE_SELECT,
      options: bankAccountOptions,
      isRequired: true,
      isEditable: Boolean(selectedAccountId),
      placeholder: selectedAccountId
        ? 'Select a beneficiary'
        : 'Select a sender account first',
      emptyMessage: selectedAccountId
        ? 'No beneficiaries match the sender currency'
        : 'Select a sender account first'
    },
    {
      name: 'referenceId',
      label: 'Reference ID',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: false,
      isEditable: true,
      placeholder: senderAccountReference
        ? `Optional — e.g. ${senderAccountReference}-TR-001`
        : 'Optional — leave blank to auto-generate',
      tooltipLabel:
        'Unique reference for this transfer. Stored on the backend; if left blank, the backend will auto-generate one.',
      maxLength: TRANSFER_REFERENCE_ID_MAX_LENGTH
    },
    {
      name: 'amount',
      label: 'Amount',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: 'text',
      isRequired: true,
      isEditable: true,
      placeholder: 'Enter amount'
    },
    {
      name: 'currency',
      label: 'Currency',
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      isRequired: true,
      isEditable: false,
      value: selectedCurrency,
      placeholder:
        selectedCurrency || 'Currency will be set based on selected account'
    }
  ];
};
